import { Subscribable, Subscription } from '@benzinga/subscribable';
import { SafeError, SafePromise, SafeType } from '@benzinga/safe-await';
import { AuthenticationManager, Permission } from '@benzinga/session';
import { PermissionRequest, PermissionRequestEvent } from './request';
import { Session } from '@benzinga/session';
import { deepEqual } from '@benzinga/utils';

export type { Permission } from '@benzinga/session';

interface PermissionChangedEvent {
  permissions: Permission[] | undefined;
  type: 'permission_changed';
}

export type PermissionsManagerEvent = PermissionRequestEvent | PermissionChangedEvent;

export class PermissionsManager extends Subscribable<PermissionsManagerEvent> {
  private request: PermissionRequest;
  private requestSubscription?: Subscription<PermissionRequest>;
  // True private fields (not TS `private`): unreachable from devtools or userscripts holding the instance.
  #permissions?: Permission[];
  #cache = new Map<string, Map<string, boolean>>([]);

  constructor(session: Session) {
    super();
    this.request = new PermissionRequest(session);
    const authenticationManger = session.getManager(AuthenticationManager);
    const authenticationSubscription = authenticationManger.subscribe(event => {
      switch (event.type) {
        case 'authentication:session_update':
          if (event.auth?.user.permissions) {
            this.#setPermission(event.auth?.user.permissions);
          } else {
            this.getPermissions(true);
          }
          break;
      }
    });
    session.onPreSessionClose(() => authenticationSubscription.unsubscribe());
    const auth = authenticationManger.getAuthentication();
    this.#permissions = auth?.user?.permissions;
    this.lockMethods(authenticationManger);
  }

  // Replacing these on the instance (e.g. `hasAccess = () => ({ ok: true })`) is how userscripts
  // unlock the UI. Make them read-only; an attempted overwrite is ignored and reported.
  private lockMethods = (authenticationManager: AuthenticationManager): void => {
    (['hasAccess', 'getPermissions'] as const).forEach(name => {
      const method = this[name];
      Object.defineProperty(this, name, {
        configurable: false,
        enumerable: true,
        get: () => method,
        set: () => authenticationManager.reportTamper('permission_manager_patched', { method: name }),
      });
    });
  };

  public static getName = () => 'benzinga-permissions';

  private static patternToRegExp = (pattern: string): RegExp => {
    const reRegExpChar = /[\\^$.+?()[\]{}|]/g;
    const reGlobChar = /[*]/g;
    const reHashChar = /[#]/g;

    return new RegExp(pattern.replace(reRegExpChar, '\\$&').replace(reGlobChar, '[^/]+').replace(reHashChar, '.+'));
  };

  private static matchRule = (rule: { action: string; resource: string }, action: string, resource: string) => {
    const match = (rule: string, action: string) =>
      action === '#' || rule === action || !!action.match(PermissionsManager.patternToRegExp(rule))?.length;
    return match(rule.action, action) && match(rule.resource, resource);
  };

  public getPermissions = async (force?: boolean): SafePromise<Permission[]> => {
    if (this.#permissions === undefined || force) {
      const permissions = await this.request.getPermissions();
      if (permissions.ok) {
        this.#setPermission(permissions.ok);
      }
      return permissions;
    } else {
      return { ok: this.#permissions };
    }
  };

  public hasAccess = (action: string, resource = '#'): SafeType<boolean> => {
    const actions = this.#cache.get(action);
    if (actions) {
      const ok = actions.get(resource);
      if (ok !== undefined) {
        return { ok };
      }
    }
    const val = this.#hasAccessPrivate(action, resource);
    if (val.ok) {
      if (actions) {
        actions.set(resource, val.ok);
      } else {
        this.#cache.set(action, new Map([[resource, val.ok]]));
      }
    }
    return val;
  };

  protected onFirstSubscription(): void {
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.requestSubscription?.unsubscribe();
  }

  #hasAccessPrivate = (action: string, resource = '#'): SafeType<boolean> => {
    if (this.#permissions === undefined || this.#permissions === null) {
      return { err: new SafeError('must be logged in to get permissions', 'permission') };
    } else {
      const matches = this.#permissions
        .filter(rule => PermissionsManager.matchRule(rule, action, resource))
        .filter(rule => rule.effect === 'allow');
      const match = matches.reduce<undefined | Permission>(
        (acc, rule) =>
          (acc === undefined && rule.action.length <= action.length) ||
          (acc !== undefined && rule.action.length > acc.action.length && rule.action.length <= action.length)
            ? rule
            : acc,
        undefined,
      );
      if (match) {
        return {
          ok: match.effect === 'allow' && (match.resource === '#' || match.resource === resource),
        };
      }
    }
    return { ok: false };
  };

  #setPermission = (permissions: Permission[] | undefined): boolean => {
    if (deepEqual(permissions, this.#permissions) === false) {
      this.#cache.clear();
      this.#permissions = permissions;
      this.dispatch({
        permissions: this.#permissions,
        type: 'permission_changed',
      });
      return true;
    }
    return false;
  };
}
